import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Form, Input, Button, Table, Spin, Card, Alert, Select, Checkbox } from 'antd';
import { PlayCircleOutlined } from '@ant-design/icons';
import { SqlQueryParameter } from '../../types';
import { ScriptManagementStore } from '../../stores';

const { Option } = Select;

type Props = {
    store: ScriptManagementStore;
    getSqlBody: () => string;
};

const DryRunModal: React.FC<Props> = ({ store, getSqlBody }) => {
    const visible = store.isDryRunModalVisible;
    const [form] = Form.useForm();
    const [isExecuting, setIsExecuting] = React.useState(false);
    const [parameters, setParameters] = React.useState<string[]>([]);

    React.useEffect(() => {
        if (visible) {
            const sqlBody = getSqlBody();
            const paramMatches = sqlBody.match(/@Request\w+/g);
            const uniqueParams = paramMatches ? Array.from(new Set(paramMatches)) : [];
            setParameters(uniqueParams);

            const initialValues: Record<string, string> = {};
            uniqueParams.forEach(param => {
                initialValues[param] = '';
            });
            form.setFieldsValue(initialValues);

            store.resetDryRunResults();

            store.fetchEnvironments();
        }
    }, [visible, form, getSqlBody, store]);

    const handleCustomEndpointChange = (checked: boolean) => {
        store.setUseCustomEndpoint(checked);
    };

    const handleEndpointUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const url = e.target.value;
        store.setCustomEndpointUrl(url);
    };

    const handleEndpointUrlBlur = () => {
        if (store.useCustomEndpoint && store.customEndpointUrl) {
            store.fetchEnvironments();
        }
    };

    const handleExecute = async () => {
        try {
            await form.validateFields();
            const values = form.getFieldsValue();
            const params: SqlQueryParameter[] = parameters.map(param => ({
                parameter: param.replace(/^@/, ''),
                value: values[param] === '' ? null : values[param]
            }));

            setIsExecuting(true);

            const sqlBody = getSqlBody();

            await store.executeDryRun(sqlBody, params);
        } catch (error) {
            console.error('Validation or execution failed:', error);
        } finally {
            setIsExecuting(false);
        }
    };

    const handleClose = () => {
        form.resetFields();
        store.closeDryRunModal();
    };

    const renderParameterInputs = () => {
        if (parameters.length === 0) {
            return (
                <Card size="small">
                    <div style={{ color: '#8c8c8c', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                        No parameters found in the SQL script. Click Execute to run the query without parameters.
                    </div>
                </Card>
            );
        }

        return (
            <div>
                {parameters.map(param => (
                    <Form.Item key={param} name={param} label={param}>
                        <Input placeholder={`Enter value for ${param}`} />
                    </Form.Item>
                ))}
            </div>
        );
    };

    const renderResults = () => {
        if (store.testRunError) {
            return (
                <div style={{ marginTop: 24 }}>
                    <div style={{ marginBottom: 8, fontWeight: 500 }}>Error</div>
                    <Alert message="Dry Run Failed" description={store.testRunError} type="error" showIcon />
                </div>
            );
        }

        if (!store.columns || store.columns.length === 0) {
            if (store.results.length === 0 && !store.testRunError) {
                return null;
            }
            return (
                <Card size="small">
                    <div style={{ color: '#8c8c8c', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
                        Query executed successfully but returned no results.
                    </div>
                </Card>
            );
        }

        const columns = store.columns.map((col: string) => ({
            title: col,
            dataIndex: col,
            key: col,
            ellipsis: true,
            render: (text: unknown) => {
                if (text === null || text === undefined) {
                    return <span style={{ color: '#8c8c8c', fontStyle: 'italic' }}>NULL</span>;
                }
                return String(text);
            }
        }));

        const dataSource =
            store.results?.map((row: Record<string, unknown>, index: number) => ({
                key: index,
                ...row
            })) || [];

        return (
            <div style={{ marginTop: 24 }}>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>Results</div>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    size="small"
                    scroll={{ x: 'max-content', y: 400 }}
                    pagination={false}
                />
            </div>
        );
    };

    return (
        <Modal
            title="Dry Run - Execute SQL Query"
            visible={visible}
            onCancel={handleClose}
            width={800}
            footer={[
                <Button key="close" onClick={handleClose}>
                    Close
                </Button>,
                <Button
                    key="execute"
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={handleExecute}
                    loading={isExecuting}
                >
                    Execute
                </Button>
            ]}
        >
            <Spin spinning={isExecuting} tip="Executing query...">
                <Form
                    form={form}
                    layout="horizontal"
                    labelCol={{ span: 6, style: { textAlign: 'left' } }}
                    wrapperCol={{ span: 18 }}
                    labelAlign="left"
                >
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8 }}>
                            <Checkbox
                                checked={store.useCustomEndpoint}
                                onChange={e => handleCustomEndpointChange(e.target.checked)}
                            >
                                Customize endpoint
                            </Checkbox>
                        </div>
                        {store.useCustomEndpoint && (
                            <div style={{ marginTop: 8 }}>
                                <Input
                                    placeholder="Enter custom endpoint URL"
                                    value={store.customEndpointUrl}
                                    onChange={handleEndpointUrlChange}
                                    onBlur={handleEndpointUrlBlur}
                                    onPressEnter={handleEndpointUrlBlur}
                                />
                            </div>
                        )}
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>Environment</div>
                        <Select
                            value={store.selectedEnvironmentId}
                            onChange={(value: string) => store.setSelectedEnvironmentId(value)}
                            loading={store.isLoadingEnvironments}
                            style={{ width: '100%' }}
                            placeholder="Select an environment"
                        >
                            {store.environments.map(env => (
                                <Option key={env.id} value={env.id}>
                                    {env.name}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <div style={{ marginBottom: 8, fontWeight: 500 }}>Parameters</div>
                        {renderParameterInputs()}
                    </div>
                </Form>

                {renderResults()}
            </Spin>
        </Modal>
    );
};

export default observer(DryRunModal);
